import { Card, CardContent } from '@/components/ui/card';
import { SITE_CONFIG } from '@/lib/constants';

/**
 * Accessibility Statement page
 */
export function Accessibility() {
  const lastUpdated = '16/12/2025';

  return (
    <section className="section-padding">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          <span className="category-label">Legal</span>
          <h1 className="text-4xl font-bold">Accessibility Statement</h1>
          <p className="mt-4 text-muted-foreground">
            Last updated: {lastUpdated}
          </p>

          <Card className="mt-8">
            <CardContent className="prose prose-invert max-w-none p-6 md:p-8">
              <div className="space-y-8">
                <section>
                  <h2 className="text-xl font-semibold text-foreground">1. Our Commitment</h2>
                  <p className="mt-2 text-muted-foreground">
                    {SITE_CONFIG.name} is committed to ensuring digital accessibility
                    for people with disabilities. We are continually improving the user
                    experience for everyone and applying the relevant accessibility
                    standards.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-foreground">2. Conformance Status</h2>
                  <p className="mt-2 text-muted-foreground">
                    We aim to conform to the Web Content Accessibility Guidelines (WCAG)
                    2.1 Level AA. These guidelines explain how to make web content more
                    accessible for people with disabilities.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-foreground">3. Accessibility Features</h2>
                  <p className="mt-2 text-muted-foreground">
                    Our website includes the following accessibility features:
                  </p>
                  <ul className="mt-2 list-disc pl-5 text-muted-foreground space-y-2">
                    <li>
                      <strong className="text-foreground">Keyboard Navigation:</strong> All
                      interactive elements are accessible via keyboard
                    </li>
                    <li>
                      <strong className="text-foreground">Skip Links:</strong> A "Skip to main
                      content" link is available for keyboard users
                    </li>
                    <li>
                      <strong className="text-foreground">Semantic HTML:</strong> Proper heading
                      structure and landmarks for screen reader navigation
                    </li>
                    <li>
                      <strong className="text-foreground">Alt Text:</strong> Meaningful
                      alternative text for images
                    </li>
                    <li>
                      <strong className="text-foreground">Color Contrast:</strong> Text and
                      interactive elements meet WCAG contrast requirements
                    </li>
                    <li>
                      <strong className="text-foreground">Focus Indicators:</strong> Visible
                      focus states on all interactive elements
                    </li>
                    <li>
                      <strong className="text-foreground">Responsive Design:</strong> Content
                      adapts to different screen sizes and zoom levels
                    </li>
                    <li>
                      <strong className="text-foreground">Form Labels:</strong> All form inputs
                      have associated labels
                    </li>
                    <li>
                      <strong className="text-foreground">ARIA Labels:</strong> Appropriate
                      ARIA attributes for enhanced screen reader support
                    </li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-foreground">4. Known Limitations</h2>
                  <p className="mt-2 text-muted-foreground">
                    While we strive for full accessibility, some limitations may exist:
                  </p>
                  <ul className="mt-2 list-disc pl-5 text-muted-foreground space-y-1">
                    <li>
                      Gallery images may have limited descriptions where visual details
                      are complex
                    </li>
                    <li>
                      Some third-party content or embedded features may not be fully
                      accessible
                    </li>
                  </ul>
                  <p className="mt-4 text-muted-foreground">
                    We are actively working to address these limitations and improve
                    accessibility throughout the site.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-foreground">5. Assistive Technologies</h2>
                  <p className="mt-2 text-muted-foreground">
                    Our website is designed to be compatible with:
                  </p>
                  <ul className="mt-2 list-disc pl-5 text-muted-foreground space-y-1">
                    <li>Screen readers (VoiceOver, NVDA, JAWS)</li>
                    <li>Screen magnification software</li>
                    <li>Speech recognition software</li>
                    <li>Keyboard-only navigation</li>
                    <li>Browser zoom functionality</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-foreground">6. Browser Compatibility</h2>
                  <p className="mt-2 text-muted-foreground">
                    We recommend using the latest versions of the following browsers
                    for the best experience:
                  </p>
                  <ul className="mt-2 list-disc pl-5 text-muted-foreground space-y-1">
                    <li>Google Chrome</li>
                    <li>Mozilla Firefox</li>
                    <li>Safari</li>
                    <li>Microsoft Edge</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-foreground">7. Feedback</h2>
                  <p className="mt-2 text-muted-foreground">
                    We welcome your feedback on the accessibility of this website.
                    If you encounter any accessibility barriers or have suggestions
                    for improvement, please contact us:
                  </p>
                  <ul className="mt-4 space-y-2 text-muted-foreground">
                    <li>
                      <strong className="text-foreground">Email:</strong>{' '}
                      <a
                        href={`mailto:${SITE_CONFIG.contact.email}`}
                        className="text-primary hover:underline"
                      >
                        {SITE_CONFIG.contact.email}
                      </a>
                    </li>
                  </ul>
                  <p className="mt-4 text-muted-foreground">
                    We aim to respond to accessibility feedback within 5 business days.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-foreground">8. Alternative Access</h2>
                  <p className="mt-2 text-muted-foreground">
                    If you are unable to access any content or functionality on this
                    website, please contact us directly. We will work with you to
                    provide the information or service you need in an accessible
                    format.
                  </p>
                </section>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
